import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  AiAgentWebsiteSource,
  AiAgentWebsiteSourceOrderByInput,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { TablePagination } from 'src/components/general/display/table-pagination';
import { TableBodyPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import {
  KnowledgeSourceContainer,
  KnowledgeSourceEmptyState,
  KnowledgeSourceHeader,
} from 'src/components/pages/ai-agents/components/knowledge-tabs';
import { AiAgentsListVariables } from 'src/components/pages/ai-agents/hooks/use-ai-agents-list-query-params';
import { useKnowledgeSourceListState } from 'src/components/pages/ai-agents/hooks/use-knowledge-source-list-state';
import { WebsiteDetailsDrawer } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/components/website-details-drawer';
import { WebsiteFormModal } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/components/website-form-modal';
import { WebsiteSourcesTable } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/components/website-sources-table';
import { useDeleteWebsiteSource } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/hooks/use-delete-website-source';
import { WEBSITE_SOURCES_LIST_QUERY } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/queries';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

type WebsiteSourcesListVariables =
  AiAgentsListVariables<AiAgentWebsiteSourceOrderByInput> & {
    knowledgeBaseId: string;
  };

export type WebsiteFormTarget =
  | { mode: 'add' }
  | { mode: 'edit'; website: AiAgentWebsiteSource };

export const WebsiteSources = memo<{
  knowledgeBaseId: string;
  aiAgentId?: string;
}>(({ knowledgeBaseId, aiAgentId }) => {
  const { t } = useTranslation(['aiAgents']);
  const { id: companyId } = useCurrentInstitution();
  const { hasPermission } = useAuthorization();
  const handleGraphQLError = useHandleGraphQLError();
  const canCreate = hasPermission('ai:company.ai_agent_knowledge_base:create');
  const { sortData, listVariables, onTableChange, getPaginationProps } =
    useKnowledgeSourceListState<AiAgentWebsiteSource>();
  const [formTarget, setFormTarget] = useState<WebsiteFormTarget | null>(null);
  const [drawerWebsiteId, setDrawerWebsiteId] = useState<string | null>(null);

  const variables: WebsiteSourcesListVariables = {
    companyId,
    knowledgeBaseId,
    ...listVariables,
  };

  const { data, loading, refetch } = useQuery<
    Pick<Query, 'aiAgents'>,
    WebsiteSourcesListVariables
  >(WEBSITE_SOURCES_LIST_QUERY, { variables, onError: handleGraphQLError });

  const openDelete = useDeleteWebsiteSource({
    aiAgentId,
    onDeleted: () => {
      setDrawerWebsiteId(null);
      void refetch();
    },
  });

  if (loading) {
    return <TableBodyPlaceholder />;
  }

  if (!data) {
    return null;
  }

  const { nodes, totalCount, pageInfo } = data.aiAgents.listWebsiteSources;
  const openAdd = canCreate ? () => setFormTarget({ mode: 'add' }) : undefined;

  return (
    <KnowledgeSourceContainer data-lgg-id="website-sources">
      {totalCount === 0 ? (
        <KnowledgeSourceEmptyState
          sourceKey="websites"
          canAdd={canCreate}
          onAdd={openAdd}
        />
      ) : (
        <>
          <KnowledgeSourceHeader
            testId="website-sources"
            count={totalCount}
            countLabel={t('aiAgents:knowledge.websites.count', { count: totalCount })}
            addLabel={t('aiAgents:knowledge.websites.addButton')}
            onAdd={openAdd}
          />
          <WebsiteSourcesTable
            data={nodes}
            sortData={sortData}
            onOpen={({ id }) => setDrawerWebsiteId(id)}
            onEdit={(website) => setFormTarget({ mode: 'edit', website })}
            onDelete={openDelete}
            onChange={onTableChange}
          />
          {(pageInfo.hasPreviousPage || pageInfo.hasNextPage) && (
            <TablePagination {...getPaginationProps(pageInfo)} />
          )}
        </>
      )}
      {formTarget && (
        <WebsiteFormModal
          knowledgeBaseId={knowledgeBaseId}
          target={formTarget}
          onClose={() => setFormTarget(null)}
          onSaved={() => {
            setFormTarget(null);
            void refetch();
          }}
        />
      )}
      {drawerWebsiteId && (
        <WebsiteDetailsDrawer
          websiteId={drawerWebsiteId}
          onClose={() => setDrawerWebsiteId(null)}
          onEdit={(website) => setFormTarget({ mode: 'edit', website })}
          onDelete={openDelete}
        />
      )}
    </KnowledgeSourceContainer>
  );
});
